/**
 * @zendir/ui - Center Layout Primitive
 *
 * Centers content horizontally and vertically using flexbox.
 * Common in operator dashboards for loading states, empty states, and hero sections.
 *
 * @example
 * ```tsx
 * <Center height="100vh">
 *   <VStack spacing="md" align="center">
 *     <Icon name="satellite" size={64} />
 *     <H2>No Telemetry Data</H2>
 *     <Body2>Connect to a spacecraft to begin monitoring.</Body2>
 *     <Button>Connect</Button>
 *   </VStack>
 * </Center>
 * ```
 */

import React, { memo, forwardRef } from "react";

export interface CenterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Element type to render */
  as?: React.ElementType;
  /** Width (CSS value or number for px) */
  width?: string | number;
  /** Height (CSS value or number for px) */
  height?: string | number;
  /** Max width constraint */
  maxWidth?: string | number;
  /** Also center text */
  textAlign?: boolean;
  /** Inline-flex instead of flex */
  inline?: boolean;
  /** Children */
  children?: React.ReactNode;
}

export const Center = memo(
  forwardRef<HTMLDivElement, CenterProps>(function Center(
    {
      as: Component = "div",
      width,
      height,
      maxWidth,
      textAlign = false,
      inline = false,
      style,
      children,
      ...rest
    },
    ref,
  ): React.ReactElement {
    const resolveSize = (
      val: string | number | undefined,
    ): string | undefined => {
      if (val === undefined) return undefined;
      if (typeof val === "number") return `${val}px`;
      return val;
    };

    const computedStyle: React.CSSProperties = {
      display: inline ? "inline-flex" : "flex",
      alignItems: "center",
      justifyContent: "center",
      width: resolveSize(width),
      height: resolveSize(height),
      maxWidth: resolveSize(maxWidth),
      textAlign: textAlign ? "center" : undefined,
      ...style,
    };

    return (
      <Component ref={ref} style={computedStyle} {...rest}>
        {children}
      </Component>
    );
  }),
);

export default Center;
